import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { formatTime } from "../../lib/format";
import { Card } from "../../components/ui/card";
import { joinTenant, useSocketEvent } from "../../hooks/use-socket";
import type { Call } from "@shared/types";

type CallEvent = { id: string; type: string; name: string; payloadJson: string | null; createdAt: number };

interface Detail {
  call: Call;
  events: CallEvent[];
  transcript: Array<{ role: "user" | "assistant"; text: string; at: number }>;
}

export function CallDetail() {
  const { callId } = useParams();
  const [detail, setDetail] = useState<Detail | null>(null);

  useEffect(() => {
    if (!callId) return;
    api<Detail>(`/api/calls/${callId}`).then(setDetail).catch(() => setDetail(null));
  }, [callId]);

  // Live : on rejoint la room du tenant pour recevoir les événements de CET appel.
  const tenantId = detail?.call.tenantId;
  useEffect(() => {
    if (tenantId) joinTenant(tenantId);
  }, [tenantId]);

  // Chaque tour (user/assistant) et chaque tool diffusé par le relay est ajouté
  // au transcript et aux événements sans recharger la page.
  const onCallEvent = useCallback(
    (ev: CallEvent & { callId: string }) => {
      if (ev.callId !== callId) return;
      setDetail((prev) => {
        if (!prev || prev.events.some((e) => e.id === ev.id)) return prev;
        const events = [...prev.events, { id: ev.id, type: ev.type, name: ev.name, payloadJson: ev.payloadJson, createdAt: ev.createdAt }];
        let transcript = prev.transcript;
        if (ev.type === "user_turn" || ev.type === "assistant_turn") {
          try {
            const text = (JSON.parse(ev.payloadJson ?? "{}") as { text?: string }).text;
            if (text) {
              transcript = [...prev.transcript, { role: ev.type === "user_turn" ? "user" : "assistant", text, at: ev.createdAt }];
            }
          } catch {
            // payload non parsable : on garde juste l'événement
          }
        }
        return { ...prev, events, transcript };
      });
    },
    [callId],
  );
  useSocketEvent<CallEvent & { callId: string }>("call:event", onCallEvent);

  if (!detail) return <div>Chargement…</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <div>
        <Link to="/calls" className="btn btn--ghost btn--sm">
          ← Retour aux appels
        </Link>
      </div>
      <header className="page-header">
        <h1 className="page-header__title">Appel {detail.call.fromNumber || "—"}</h1>
      </header>

      <Card title="Métadonnées">
        <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "0.5rem 1.5rem" }}>
          <dt>Status</dt>
          <dd>{detail.call.status}</dd>
          <dt>Durée</dt>
          <dd>{detail.call.durationSec ?? 0} sec</dd>
          <dt>Intent</dt>
          <dd>{detail.call.intent ?? "—"}</dd>
          <dt>Sentiment</dt>
          <dd>{detail.call.sentiment ?? "—"}</dd>
          <dt>Coût</dt>
          <dd>{detail.call.costUsd ? `$${detail.call.costUsd.toFixed(3)}` : "—"}</dd>
        </dl>
      </Card>

      {detail.call.recordingUrl ? (
        <Card title="Enregistrement">
          <audio controls src={detail.call.recordingUrl} style={{ width: "100%" }} />
        </Card>
      ) : null}

      {detail.call.summary ? (
        <Card title="Résumé">
          <p>{detail.call.summary}</p>
        </Card>
      ) : null}

      <Card title="Transcript">
        <div className="transcript">
          {detail.transcript.length === 0 ? (
            <p>Pas de transcription disponible.</p>
          ) : (
            detail.transcript.map((t, i) => (
              <div key={i} className={`transcript__turn transcript__turn--${t.role}`}>
                <span className="transcript__role">{t.role === "user" ? "Caller" : "Agent"}</span>
                <p>{t.text}</p>
              </div>
            ))
          )}
        </div>
      </Card>

      <Card title="Événements">
        <table className="table">
          <thead>
            <tr>
              <th>Heure</th>
              <th>Type</th>
              <th>Nom</th>
            </tr>
          </thead>
          <tbody>
            {detail.events.map((e) => (
              <tr key={e.id}>
                <td>{formatTime(e.createdAt)}</td>
                <td>{e.type}</td>
                <td>{e.name}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
